import { Component, ElementRef, Input, OnChanges, OnInit, AfterViewInit, OnDestroy, SimpleChanges, ViewChild, ChangeDetectionStrategy, signal, effect } from '@angular/core';
import { lastValueFrom, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { LoadCaseSummaryResult } from 'src/app/perivis/models/load-case.model';
import { LoadCaseService } from 'src/app/perivis/services/load-case.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { ExpansionJointFocus, PackerSchematicDisplayMode, Packers } from 'src/app/wellbore-inputs/models/packers.model';
import { PackerService } from 'src/app/wellbore-inputs/services/packers.service';
import * as ViewBoxHelpers from '../../well-schematic/helpers/viewbox.helper';
import * as PackerSchematicHelpers from './helpers/packer-schematic.helper';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { copySchematicToClipboard } from 'src/app/shared/helpers/schematic-export.helper';

@Component({
  selector: 'app-packer-schematic',
  templateUrl: './packer-schematic.component.html',
  styleUrls: ['./packer-schematic.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PackerSchematicComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @Input() packerIndex!: number;
  @Input() tubularId!: string;
  @Input() packerName!: string;

  @ViewChild('schematicContainer', { static: true })
  public schematicContainer!: ElementRef;

  @ViewChild('packerCm', { static: false })
  public packerCm!: ContextMenu;

  public readonly packerContextMenuItems = signal<MenuItem[]>([
    {
      label: 'Copy schematic as image',
      icon: PrimeIcons.COPY,
      command: () => void this.copySchematicAsImage(),
    }
  ]);

  public svgContent = signal<string>('');
  public isLoading = signal<boolean>(false);
  public errorMessage = signal<string | null>(null);
  public displayMode = signal<PackerSchematicDisplayMode>(PackerSchematicDisplayMode.TubingToPackerForces);
  public expansionJointFocus = signal<ExpansionJointFocus>(ExpansionJointFocus.ExpansionJoint);
  public selectedLoadCase = signal<number | null>(null);
  public loadCases = signal<Array<{ label: string; value: number }>>([]);
  public showExpansionJointFocusToggle = signal<boolean>(false);
  public expansionJointFocusOptions = [
    { label: 'Exp. Joint', value: ExpansionJointFocus.ExpansionJoint },
    { label: 'Packer Body', value: ExpansionJointFocus.PackerBody }
  ];
  public displayModeOptions = [
    { label: 'Tubing to Packer Forces', value: PackerSchematicDisplayMode.TubingToPackerForces },
    { label: 'Latching Force', value: PackerSchematicDisplayMode.LatchingForce },
    { label: 'Packer to Casing Force', value: PackerSchematicDisplayMode.PackerToCasingForce },
    { label: 'Cross Sectional Area', value: PackerSchematicDisplayMode.CrossSectionalAreas }
  ];

  // SVG pan/zoom state
  public isPanning = signal<boolean>(false);
  private svgElement!: SVGSVGElement;
  private viewBox: ViewBoxHelpers.ViewBox = { x: 0, y: 0, width: 1000, height: 1000 };
  private originalViewBox: ViewBoxHelpers.ViewBox = { x: 0, y: 0, width: 1000, height: 1000 };
  private scale = 1;
  private panStart: ViewBoxHelpers.Point = { x: 0, y: 0 };

  // Resize handling
  private _resizeObserver: ResizeObserver | null = null;
  private _drawSchematic$ = new Subject<void>();
  private _destroy$ = new Subject<void>();

  constructor(
    private _packerService: PackerService,
    private _elementRef: ElementRef,
    private _signalRService: SignalRService,
    private _loadCaseService: LoadCaseService,
    private _toaster: AppNotificationService
  ) {
    // Debounced drawing - waits 500ms after last trigger
    this._drawSchematic$.pipe(
      debounceTime(500),
      takeUntil(this._destroy$)
    ).subscribe(() => this.loadSchematic());

    // Watch svgContent signal and attach listeners when it changes
    effect(() => {
      const svg = this.svgContent();
      if (svg) {
        queueMicrotask(() => this.attachSvgEventListeners());
      }
    });
  }

  ngOnInit(): void {
    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  ngAfterViewInit(): void {
    // Observe the host element for resize events
    this._resizeObserver = new ResizeObserver(() => this.onContainerResize());
    this._resizeObserver.observe(this._elementRef.nativeElement);
  }

  private onContainerResize(): void {
    // Immediately re-fit existing SVG content
    if (this.svgElement && this.svgContent()) {
      this.resetView();
    }
    // Request new schematic at new dimensions (debounced)
    this.requestDraw();
  }

  private requestDraw(): void {
    this._drawSchematic$.next();
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    this._destroy$.next();
    this._destroy$.complete();
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS || data.action == PeriforOnChangeMessages.REFRESH_PACKERS) {
      this.loadSchematic();
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['packerIndex']) {
      this.expansionJointFocus.set(ExpansionJointFocus.ExpansionJoint);
    }

    if ((changes['packerIndex'] || changes['tubularId']) && this.packerIndex !== undefined) {
      await this.loadSchematic();
    }
  }

  public async loadSchematic(): Promise<void> {
    if (this.packerIndex === undefined || this.packerIndex < 0) {
      this.errorMessage.set('No packer selected');
      this.svgContent.set('');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      // Get dimensions from GridStack wrapper for accurate sizing
      const hostElement = this._elementRef.nativeElement as HTMLElement;
      const gridStackWrapper = hostElement.closest('.grid-stack-item-content') as HTMLElement;
      const measureFrom = gridStackWrapper || hostElement;
      const rect = measureFrom.getBoundingClientRect();
      
      const width = Math.floor(rect.width) || 600;
      const height = Math.floor(rect.height) || 600;

      const [packersData, loadCases] = await Promise.all([
        lastValueFrom(this._packerService.getPackersForTubular(this.tubularId)),
        lastValueFrom(this._loadCaseService.getLoadCasesSummary(true))
      ]);

      this.updateExpansionJointFocusAvailability(packersData as Packers);

      const loadCaseOptions = (loadCases as LoadCaseSummaryResult[])?.map(lc => ({
                label: lc.name,
                value: lc.index
              })) || [];
      this.loadCases.set(loadCaseOptions);

      const currentLoadCases = this.loadCases();
      if (this.selectedLoadCase() === null && currentLoadCases.length > 0) {
        this.selectedLoadCase.set(currentLoadCases[0].value);
      }

      const svg = await lastValueFrom(
        this._packerService.getPackerSchematic(
          this.packerIndex,
          this.displayMode(),
          this.tubularId,
          undefined,
          width,
          height,
          this.selectedLoadCase() ?? undefined,
          this.expansionJointFocus()
        )
      );
      
      if (svg && svg.includes('<svg')) {
        // Keep original SVG attributes, let CSS handle sizing
        this.svgContent.set(svg);
        // Effect in constructor will attach listeners when svgContent changes
      } else {
        this.errorMessage.set(svg || 'No schematic data available');
        this.svgContent.set('');
      }
    } catch (error: any) {
      console.error('Error loading packer schematic:', error);
      this.errorMessage.set(PackerSchematicHelpers.formatErrorMessage(error));
      this.svgContent.set('');
    } finally {
      this.isLoading.set(false);
    }
  }

  public async onDisplayModeOrLoadCaseChange(): Promise<void> {
    await this.loadSchematic();
  }

  public async onExpansionJointFocusChange(): Promise<void> {
    await this.loadSchematic();
  }

  private updateExpansionJointFocusAvailability(packersData: Packers | null): void {
    const shouldShow = PackerSchematicHelpers.shouldShowExpansionJointToggle(packersData, this.packerIndex);
    this.showExpansionJointFocusToggle.set(shouldShow);

    if (!shouldShow) {
      this.expansionJointFocus.set(ExpansionJointFocus.ExpansionJoint);
    }
  }

  private attachSvgEventListeners() {
    const container = this.schematicContainer?.nativeElement;
    if (!container) return;

    this.svgElement = container.querySelector('svg') as SVGSVGElement;
    if (!this.svgElement) return;

    // Force SVG to center content and scale uniformly
    this.svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Store original viewBox for reset
    const viewBoxAttr = this.svgElement.getAttribute('viewBox');
    if (viewBoxAttr) {
      const parsed = ViewBoxHelpers.parseViewBoxString(viewBoxAttr);
      if (parsed) {
        this.viewBox = parsed;
        this.originalViewBox = parsed;
        this.scale = 1;
        
        // Auto-fit content on initial load so schematic displays at proper zoom level
        const fitViewBox = this.computeContentFittedViewBox();
        if (fitViewBox) {
          this.viewBox = fitViewBox;
        }
        this.updateViewBox();
      }
    }
  }

  private computeContentFittedViewBox(): ViewBoxHelpers.ViewBox | null {
    if (!this.svgElement) return null;
    
    try {
      const bbox = this.svgElement.getBBox();
      return ViewBoxHelpers.computeContentFittedViewBox(bbox, 20);
    } catch (e) {
      console.warn('Could not compute content bounding box:', e);
      return null;
    }
  }

  onMouseDown = (event: MouseEvent) => {
    this.isPanning.set(true);
    this.panStart = { x: event.clientX, y: event.clientY };
  }

  onMouseUp = () => {
    this.isPanning.set(false);
  }

  onMouseMove(event: MouseEvent) {
    if (this.isPanning() && this.svgElement) {
      const currentPosition: ViewBoxHelpers.Point = { x: event.clientX, y: event.clientY };
      this.viewBox = ViewBoxHelpers.computePanViewBox(
        this.viewBox,
        this.panStart,
        currentPosition,
        this.scale
      );
      this.updateViewBox();
      this.panStart = currentPosition;
      event.preventDefault();
    }
  }

  onMouseWheel(event: WheelEvent) {
    if (!this.svgElement) return;

    event.preventDefault();

    if (event.shiftKey) {
      // Pan with shift+scroll
      this.viewBox = ViewBoxHelpers.computeShiftPanViewBox(
        this.viewBox,
        event.deltaX,
        event.deltaY,
        this.scale
      );
      this.updateViewBox();
    } else {
      // Zoom around mouse cursor
      const svgRect = this.svgElement.getBoundingClientRect();
      const mousePosition: ViewBoxHelpers.Point = { x: event.clientX, y: event.clientY };
      
      this.viewBox = ViewBoxHelpers.computeZoomViewBox(
        this.viewBox,
        mousePosition,
        svgRect,
        event.deltaY
      );
      this.scale = ViewBoxHelpers.computeNewScale(this.scale, event.deltaY);
      this.updateViewBox();
    }
  }

  resetView() {
    // Reset to original viewBox (zoom to extents)
    if (this.svgElement && this.originalViewBox) {
      this.viewBox = { ...this.originalViewBox };
      this.scale = 1;
      this.updateViewBox();
    }
  }

  public async onExportPng(): Promise<void> {
    if (!this.svgElement) return;

    await PackerSchematicHelpers.downloadSvgAsPng(
      this.svgElement,
      `${this.packerName}_schematic.png`
    );
  }

  public onPackerContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.packerCm?.show(event);
  }

  public async copySchematicAsImage(): Promise<void> {
    const result = await copySchematicToClipboard(this.svgElement);
    if (result.success) {
      this._toaster.showSuccess(result.message);
    } else {
      this._toaster.showError(result.message);
    }
  }

  private updateViewBox() {
    if (this.svgElement) {
      this.svgElement.setAttribute('viewBox', ViewBoxHelpers.formatViewBoxString(this.viewBox));
    }
  }
}
